import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useAlert } from '../context/AlertContext';
import WhyWhyTree from './WhyWhyTree';
import {
  MAX_WHY_WHY_NODES,
  addChild,
  buildTree,
  removeSubtree,
  subtreeIds,
  toSavePayload,
  toggleRootCause,
  updateNode,
} from '../utils/whyWhyTree';

// Why-Why Analysis TREE editor for incidents. A separate tool from the flat
// 5-Why (FiveWhyAnalysis.js), which is unchanged; this modal mirrors its
// layout and styling. Every answer can branch into further "why?"s and any
// answer can be flagged as a root cause.
//
// Backend: GET/POST `${apiBase}/{id}/rca/why-why` (incidents only; the
// server validates the whole tree and enforces incidents:read / :update).

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const NodeEditor = ({ node, depth, onChangeText, onAddChild, onToggleRootCause, onDelete, canAdd }) => (
  <li className="relative pl-6">
    <span className="absolute left-0 top-6 w-5 border-t-2 border-gray-300" aria-hidden="true" />
    <div
      className={`rounded-lg border-2 p-3 my-2 ${
        node.is_root_cause ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200'
      }`}
    >
      <label className="block text-sm font-bold text-gray-700 mb-2">
        Why? <span className="font-normal text-gray-500">(level {depth})</span>
      </label>
      <textarea
        value={node.text}
        onChange={(e) => onChangeText(node.id, e.target.value)}
        rows="2"
        maxLength={2000}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
        placeholder={depth === 1 ? 'Why did the problem occur?' : 'Why did the answer above occur?'}
        data-testid={`why-why-input-${node.id}`}
      />
      <div className="flex flex-wrap gap-2 mt-2">
        <button
          type="button"
          onClick={() => onAddChild(node.id)}
          disabled={!canAdd}
          className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-semibold flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PlusIcon /> Add "Why?" below
        </button>
        <label className="px-3 py-1.5 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors text-sm font-semibold flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={node.is_root_cause}
            onChange={() => onToggleRootCause(node.id)}
            className="w-4 h-4"
          />
          Root cause
        </label>
        <button
          type="button"
          onClick={() => onDelete(node.id)}
          className="px-3 py-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors text-sm font-semibold flex items-center gap-1"
          title="Delete this answer and everything below it"
        >
          <TrashIcon /> Delete
        </button>
      </div>
    </div>
    {node.children.length > 0 && (
      <ul className="ml-3 border-l-2 border-gray-300">
        {node.children.map((child) => (
          <NodeEditor
            key={child.id}
            node={child}
            depth={depth + 1}
            onChangeText={onChangeText}
            onAddChild={onAddChild}
            onToggleRootCause={onToggleRootCause}
            onDelete={onDelete}
            canAdd={canAdd}
          />
        ))}
      </ul>
    )}
  </li>
);

const WhyWhyAnalysis = ({ hazardId, onClose, apiBase = `${API}/incidents` }) => {
  const { showSuccess, showError, confirm } = useAlert();
  const [problemStatement, setProblemStatement] = useState('');
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    fetchAnalysis();
  }, [hazardId]);

  const fetchAnalysis = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${apiBase}/${hazardId}/rca/why-why`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProblemStatement(response.data?.problem_statement || '');
      setNodes(Array.isArray(response.data?.nodes) ? response.data.nodes : []);
    } catch (err) {
      console.error('Failed to load Why-Why analysis:', err);
    } finally {
      setLoading(false);
    }
  };

  const canAdd = nodes.length < MAX_WHY_WHY_NODES;

  const handleAddChild = (parentId) => {
    if (!canAdd) return;
    setNodes((prev) => addChild(prev, parentId));
  };

  const handleChangeText = (id, text) => {
    setNodes((prev) => updateNode(prev, id, { text }));
  };

  const handleToggleRootCause = (id) => {
    setNodes((prev) => toggleRootCause(prev, id));
  };

  const handleDelete = async (id) => {
    const nested = subtreeIds(nodes, id).size - 1;
    const confirmed = await confirm(
      'Delete this answer?',
      nested > 0
        ? `This also deletes the ${nested} answer${nested === 1 ? '' : 's'} nested below it.`
        : 'This answer will be removed from the tree.',
      { confirmLabel: 'Delete', destructive: true }
    );
    if (!confirmed) return;
    setNodes((prev) => removeSubtree(prev, id));
  };

  const handleSave = async () => {
    if (!problemStatement.trim()) {
      showError('Please enter a problem statement');
      return;
    }
    const payloadNodes = toSavePayload(nodes);
    if (payloadNodes.some((n) => !n.text)) {
      showError('Every "Why?" needs an answer — fill it in or delete it');
      return;
    }

    setSaveLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${apiBase}/${hazardId}/rca/why-why`,
        { problem_statement: problemStatement.trim(), nodes: payloadNodes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (Array.isArray(response.data?.nodes)) setNodes(response.data.nodes);
      showSuccess('Why-Why analysis saved successfully!');
    } catch (err) {
      const detail = err.response?.data?.detail;
      showError(typeof detail === 'string' ? detail : 'Failed to save analysis');
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const roots = buildTree(nodes);
  const rootCauseCount = nodes.filter((n) => n.is_root_cause).length;

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#1B5C94]">Why-Why Analysis Tree</h2>
          <p className="text-sm text-gray-600 mt-1">Branch every answer into further "Why?"s and mark the root causes you reach</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Problem Statement */}
      <div className="mb-6">
        <label className="block text-sm font-bold text-gray-700 mb-2">
          Problem Statement <span className="text-red-500">*</span>
        </label>
        <textarea
          value={problemStatement}
          onChange={(e) => setProblemStatement(e.target.value)}
          rows="2"
          maxLength={2000}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
          placeholder="Clearly state the problem you are investigating..."
          data-testid="why-why-problem-statement"
        />
      </div>

      {/* Editable tree */}
      {roots.length > 0 && (
        <ul className="mb-4 ml-3 border-l-2 border-gray-300">
          {roots.map((root) => (
            <NodeEditor
              key={root.id}
              node={root}
              depth={1}
              onChangeText={handleChangeText}
              onAddChild={handleAddChild}
              onToggleRootCause={handleToggleRootCause}
              onDelete={handleDelete}
              canAdd={canAdd}
            />
          ))}
        </ul>
      )}

      {/* Add top-level why */}
      <button
        type="button"
        onClick={() => handleAddChild(null)}
        disabled={!canAdd}
        className="mb-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="why-why-add-top-level"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        Add "Why?" for the problem
      </button>
      <p className="mb-6 text-xs text-gray-500">
        {nodes.length} / {MAX_WHY_WHY_NODES} answers · {rootCauseCount} root cause{rootCauseCount === 1 ? '' : 's'} marked
      </p>

      {/* Tree preview */}
      <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-lg p-4">
        <h4 className="text-sm font-bold text-gray-700 mb-3">Tree Preview</h4>
        <WhyWhyTree problemStatement={problemStatement} nodes={nodes} />
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={handleSave}
          disabled={saveLoading}
          className="flex-1 bg-[#1B5C94] text-white font-bold py-3 px-6 rounded-lg hover:bg-[#164A7B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="why-why-save"
        >
          {saveLoading ? 'Saving...' : 'Save Analysis'}
        </button>
        {onClose && (
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-200 text-gray-700 font-bold rounded-lg hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        )}
      </div>

      {/* Methodology Info */}
      <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4">
        <h4 className="font-bold text-blue-800 mb-2">💡 Why-Why Methodology</h4>
        <p className="text-sm text-blue-700">
          Start with the problem statement and list every direct reason it happened. For each reason ask
          "Why?" again — an answer can have several causes, so the analysis branches. Keep going down each
          branch until you reach causes you can act on, and mark those as root causes.
        </p>
      </div>
    </div>
  );
};

export default WhyWhyAnalysis;
